import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const TarafSerap = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [dataRapor, setDataRapor] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedTahunAjaran, setSelectedTahunAjaran] = useState("");
  const [selectedKelas, setSelectedKelas] = useState("");
  const [selectedRombel, setSelectedRombel] = useState("");

  /* ================= PROFIL ================= */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");

    axios
      .get(`${process.env.REACT_APP_API_URL}api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setUser(res.data))
      .catch(() => navigate("/login"));
  }, [navigate]);

  /* ================= NILAI ================= */
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem("token");

    axios
      .get(`${process.env.REACT_APP_API_URL}api/nilaiRapor/all`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setDataRapor(res.data))
      .finally(() => setLoading(false));
  }, [user]);

  /* ================= TAHUN AJARAN ================= */
  const tahunAjaranList = useMemo(() => {
    const years = [
      ...new Set(
        dataRapor
          .map((i) => parseInt(i.tahun, 10))
          .filter((year) => !isNaN(year)),
      ),
    ].sort((a, b) => a - b);

    const result = [];

    years.forEach((year) => {
      const hasSemester1 = dataRapor.some(
        (i) => i.semester === "1" && parseInt(i.tahun) === year,
      );

      const hasSemester2 = dataRapor.some(
        (i) => i.semester === "2" && parseInt(i.tahun) === year + 1,
      );

      if (hasSemester1 || hasSemester2) {
        result.push(`${year}/${year + 1}`);
      }
    });

    return result;
  }, [dataRapor]);

  /* ================= FILTER ================= */
  const filteredData = useMemo(() => {
    if (!selectedTahunAjaran) return [];

    const [tahun1, tahun2] = selectedTahunAjaran.split("/");

    return dataRapor.filter((i) => {
      const smt1 = i.semester === "1" && i.tahun === tahun1;
      const smt2 = i.semester === "2" && i.tahun === tahun2;

      if (!(smt1 || smt2)) return false;
      if (selectedKelas && i.kelas !== selectedKelas) return false;
      if (selectedRombel && i.rombel !== selectedRombel) return false;

      return true;
    });
  }, [dataRapor, selectedTahunAjaran, selectedKelas, selectedRombel]);

  /* ================= HITUNG RATA2 PER MAPEL ================= */
  const tarafSerapData = useMemo(() => {
    const result = {};

    filteredData.forEach((item) => {
      if (!result[item.mapel]) {
        result[item.mapel] = {
          mapel: item.mapel,
          total1: 0,
          total2: 0,
          count1: 0,
          count2: 0,
        };
      }

      item.nilai.forEach((siswa) => {
        if (typeof siswa.finalScore === "number") {
          if (item.semester === "1") {
            result[item.mapel].total1 += siswa.finalScore;
            result[item.mapel].count1++;
          }
          if (item.semester === "2") {
            result[item.mapel].total2 += siswa.finalScore;
            result[item.mapel].count2++;
          }
        }
      });
    });

    return Object.values(result).map((m) => ({
      mapel: m.mapel,
      semester1: m.count1 ? m.total1 / m.count1 : 0,
      semester2: m.count2 ? m.total2 / m.count2 : 0,
    }));
  }, [filteredData]);

  const kelasList = [...new Set(dataRapor.map((i) => i.kelas))];
  const rombelList = [...new Set(dataRapor.map((i) => i.rombel))];

  if (loading) return <div className="text-center py-20">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <h1 className="text-3xl font-bold text-center mb-8">
        Grafik Pencapian Taraf Serap
      </h1>

      {/* FILTER */}
      <div className="flex flex-wrap gap-4 justify-center mb-10">
        <select
          value={selectedTahunAjaran}
          onChange={(e) => setSelectedTahunAjaran(e.target.value)}
          className="border p-2 rounded"
        >
          <option value="">Pilih Tahun Ajaran</option>
          {tahunAjaranList.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>

        <select
          value={selectedKelas}
          onChange={(e) => setSelectedKelas(e.target.value)}
          className="border p-2 rounded"
        >
          <option value="">Pilih Kelas</option>
          {kelasList.map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>

        <select
          value={selectedRombel}
          onChange={(e) => setSelectedRombel(e.target.value)}
          className="border p-2 rounded"
        >
          <option value="">Pilih Rombel</option>
          {rombelList.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
      </div>

      {/* TABEL GRAFIK */}
      <div className="overflow-x-auto">
        <table className="min-w-full border text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="border p-2">Mata Pelajaran</th>
              <th className="border p-2">Semester 1</th>
              <th className="border p-2">Semester 2</th>
            </tr>
          </thead>
          <tbody>
            {tarafSerapData.map((item) => (
              <tr key={item.mapel}>
                <td className="border p-2 font-semibold">{item.mapel}</td>

                {/* Semester 1 */}
                <td className="border p-2">
                  <div className="w-full bg-gray-200 h-6 rounded">
                    <div
                      className="h-6 bg-blue-500 rounded text-white text-xs flex items-center justify-center"
                      style={{ width: `${item.semester1}%` }}
                    >
                      {item.semester1.toFixed(1)}
                    </div>
                  </div>
                </td>

                {/* Semester 2 */}
                <td className="border p-2">
                  <div className="w-full bg-gray-200 h-6 rounded">
                    <div
                      className="h-6 bg-green-500 rounded text-white text-xs flex items-center justify-center"
                      style={{ width: `${item.semester2}%` }}
                    >
                      {item.semester2.toFixed(1)}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TarafSerap;
